"use client";

import { createContext, useContext, useEffect, useSyncExternalStore, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import { isPortalLocale, isPortalTheme, portalLocaleCookie, portalThemeStorageKey, readPortalLocaleCookie, type PortalLocale, type PortalTheme } from "../lib/portalPreferences";

export type { PortalLocale, PortalTheme } from "../lib/portalPreferences";

type PortalPreferencesValue = {
  theme: PortalTheme;
  setTheme: (theme: PortalTheme) => void;
  locale: PortalLocale;
  setLocale: (locale: PortalLocale) => void;
};

const themeChanged = "portal-theme-changed";
const localeChanged = "portal-locale-changed";
let unavailableStorageTheme: PortalTheme | null = null;
let unavailableCookieLocale: PortalLocale | null = null;
const PortalPreferencesContext = createContext<PortalPreferencesValue | null>(null);

function getTheme(): PortalTheme {
  if (unavailableStorageTheme !== null) return unavailableStorageTheme;
  try {
    const value = window.localStorage.getItem(portalThemeStorageKey);
    return isPortalTheme(value) ? value : "light";
  } catch {
    return "light";
  }
}

function applyTheme(theme: PortalTheme) {
  const dark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.dataset.theme = dark ? "dark" : "light";
}

function subscribe(onChange: () => void) {
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  const update = () => {
    applyTheme(getTheme());
    onChange();
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== portalThemeStorageKey && event.key !== null) return;
    unavailableStorageTheme = null;
    update();
  };
  window.addEventListener(themeChanged, update);
  window.addEventListener("storage", onStorage);
  media.addEventListener("change", update);
  return () => {
    window.removeEventListener(themeChanged, update);
    window.removeEventListener("storage", onStorage);
    media.removeEventListener("change", update);
  };
}

function setTheme(theme: PortalTheme) {
  if (!isPortalTheme(theme)) return;
  try {
    window.localStorage.setItem(portalThemeStorageKey, theme);
    unavailableStorageTheme = null;
  } catch {
    // Theme selection still works when the browser does not allow persistence.
    unavailableStorageTheme = theme;
  }
  applyTheme(theme);
  window.dispatchEvent(new Event(themeChanged));
}

function getLocale(): PortalLocale {
  return unavailableCookieLocale ?? readPortalLocaleCookie(document.cookie);
}

function subscribeLocale(onChange: () => void) {
  window.addEventListener(localeChanged, onChange);
  window.addEventListener("focus", onChange);
  return () => {
    window.removeEventListener(localeChanged, onChange);
    window.removeEventListener("focus", onChange);
  };
}

export function PortalPreferences({ children, initialLocale = "cs" }: { children: ReactNode; initialLocale?: PortalLocale }) {
  const router = useRouter();
  const theme = useSyncExternalStore(subscribe, getTheme, () => "light" as const);
  const locale = useSyncExternalStore(subscribeLocale, getLocale, () => initialLocale);

  function setLocale(nextLocale: PortalLocale) {
    if (!isPortalLocale(nextLocale) || nextLocale === locale) return;
    document.cookie = portalLocaleCookie(nextLocale, window.location.protocol === "https:");
    unavailableCookieLocale = readPortalLocaleCookie(document.cookie) === nextLocale ? null : nextLocale;
    document.documentElement.lang = nextLocale;
    window.dispatchEvent(new Event(localeChanged));
    // Server-rendered directory content reads the same cookie as this provider.
    router.refresh();
  }

  useEffect(() => {
    // Read storage here so the hydration snapshot never overwrites the head bootstrap.
    applyTheme(getTheme());
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return <PortalPreferencesContext.Provider value={{ theme, setTheme, locale, setLocale }}>{children}</PortalPreferencesContext.Provider>;
}

export function usePortalPreferences() {
  const preferences = useContext(PortalPreferencesContext);
  if (!preferences) throw new Error("usePortalPreferences must be used inside PortalPreferences");
  return preferences;
}
